import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Directions,
  FormField,
  IntegrationThemes,
} from '@cashier/cashier-model';
import { eventBus, EventFactory } from '@cashier/cashier-shared';
import { cn } from '@cashier/cashier-ui';

import {
  useCashierConfiguration,
  useFieldToucher,
  useFormPayloadItem,
} from '../../hooks';
import { Checkbox } from './modules';

import styles from './styles.module.scss';

export interface AgreementCheckboxProps {
  field: FormField;
  theme?: IntegrationThemes;
  version: 'v2' | 'v3';
}

export const AgreementCheckbox = ({
  field,
  theme = IntegrationThemes.Default,
  version,
}: AgreementCheckboxProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const { termsUrl, host, direction } = useCashierConfiguration();

  const { touch } = useFieldToucher();

  const { isDisabled } = useFormPayloadItem(field.name);

  const getHostUrl = useMemo((): string => {
    if (host) {
      const pathArray = host.split('/');
      return pathArray[2];
    }

    return '';
  }, [host]);

  const onChange = useCallback(
    (value: boolean) => {
      touch({ ...field, value }, { interactionCompleted: true });

      eventBus.emit(EventFactory.onFieldFocused(field.name));
    },
    [field, touch],
  );

  const showAlternativeMessage = useMemo((): boolean => {
    return !(
      i18n.exists('agreement.address') && i18n.exists('agreement.phone')
    );
  }, [i18n]);

  const showAgreementSection = useMemo((): boolean => {
    return !!(t('agreement.address', '') && t('agreement.phone', ''));
  }, [t]);

  const renderAgreementSection = useMemo((): ReactElement => {
    return (
      <>
        {t('agreement.address')} {`${getHostUrl}`} {t('agreement.phone')}
      </>
    );
  }, [t, getHostUrl]);

  const renderCheckboxDescription = useMemo((): ReactElement => {
    if (theme === IntegrationThemes.Platform) {
      return showAlternativeMessage ? (
        <p
          className={cn({
            [styles.platformText]: true,
            [styles.rtl]: direction === Directions.Rtl,
          })}
        >
          {t('agreement.alternativeMessage')}
        </p>
      ) : (
        <p
          className={cn({
            [styles.platformText]: true,
            [styles.rtl]: direction === Directions.Rtl,
          })}
        >
          {t('agreement.message')}{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            className={styles.platformLink}
            href={termsUrl || '/'}
          >
            ({t('agreement.link')})
          </a>
          .
          <br />
          <br />
          {showAgreementSection && renderAgreementSection}
        </p>
      );
    }

    return showAlternativeMessage ? (
      <p>{t('agreement.alternativeMessage')}</p>
    ) : (
      <p>
        {t('agreement.message')}{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          className={styles.text}
          href={termsUrl || '/'}
        >
          ({t('agreement.link')})
        </a>
        .{showAgreementSection && renderAgreementSection}
      </p>
    );
  }, [
    theme,
    showAlternativeMessage,
    t,
    termsUrl,
    showAgreementSection,
    renderAgreementSection,
    direction,
  ]);

  return (
    <div
      className={cn({
        [styles.checkboxContainer]: true,
        [styles.platform]: theme === IntegrationThemes.Platform,
        [styles.platformV3]: version === 'v3',
      })}
    >
      <label htmlFor={field.name} data-test="agree-with-terms-text">
        {renderCheckboxDescription}
        <Checkbox
          name={field.name}
          value={field.value === true}
          isDisabled={isDisabled}
          onChange={onChange}
        />
        <span
          className={cn({
            [styles.checkmark]: theme === IntegrationThemes.Default,
            [styles.platformCheckmark]: theme === IntegrationThemes.Platform,
            [styles.rtl]: direction === Directions.Rtl,
          })}
        />
      </label>
    </div>
  );
};
